import React from 'react';
import './Terms.css';

interface Props {
  onBack?: () => void;
}

export default function PoliticaPrivacidad({ onBack }: Props) {
  return (
    <div className="terms-root">
      <div className="terms-card">
        <div className="terms-header">
          <ShieldIcon />
          <div>
            <h1 className="terms-title">Política de Privacidad</h1>
            <p className="terms-subtitle">StorSafe · AndeanSoft SAS · Versión 1.0 · Vigente desde enero 2026</p>
          </div>
        </div>

        <div className="terms-body">

          <section className="terms-section">
            <h2>1. Responsable del tratamiento</h2>
            <p>
              <strong>AndeanSoft SAS</strong>, con domicilio en Ecuador, es la empresa responsable del tratamiento de los datos
              personales recopilados a través de la plataforma <strong>StorSafe</strong>. Para consultas relacionadas con el
              tratamiento de sus datos, puede contactarnos en:{' '}
              <a href="mailto:jcifuentes@storsafe.app" style={{ color: '#2FBF71' }}>jcifuentes@storsafe.app</a>.
            </p>
          </section>

          <section className="terms-section">
            <h2>2. Datos que recopilamos</h2>
            <p>StorSafe recopila los siguientes tipos de datos personales:</p>
            <ul>
              <li><strong>Datos de identificación:</strong> nombre completo, cargo, correo electrónico y número de teléfono del usuario.</li>
              <li><strong>Datos de la empresa:</strong> nombre de la empresa, RUC/NIT, país y plan contratado.</li>
              <li><strong>Datos de uso:</strong> registros de actividad dentro de la plataforma (auditoría), direcciones IP y datos de sesión.</li>
              <li><strong>Datos técnicos:</strong> tipo de dispositivo, navegador y sistema operativo utilizados para acceder al servicio.</li>
            </ul>
            <p>No recopilamos datos sensibles conforme a lo establecido en la Ley Orgánica de Protección de Datos Personales (LOPDP) del Ecuador.</p>
          </section>

          <section className="terms-section">
            <h2>3. Finalidad del tratamiento</h2>
            <p>Los datos personales recopilados se utilizan exclusivamente para:</p>
            <ul>
              <li>Proveer, mantener y mejorar los servicios de la plataforma StorSafe.</li>
              <li>Gestionar el acceso de usuarios y el control de permisos dentro de cada empresa cliente.</li>
              <li>Enviar notificaciones relacionadas con el servicio (alertas de expiración de prueba, actualizaciones de cuenta).</li>
              <li>Cumplir con obligaciones legales y requerimientos regulatorios aplicables.</li>
              <li>Atender consultas, soporte técnico y solicitudes de los usuarios.</li>
            </ul>
            <p>No utilizamos los datos personales para fines de publicidad de terceros ni los vendemos a ninguna entidad externa.</p>
          </section>

          <section className="terms-section">
            <h2>4. Base legal del tratamiento</h2>
            <p>El tratamiento de datos personales se realiza bajo las siguientes bases legales previstas en la LOPDP Ecuador:</p>
            <ul>
              <li><strong>Ejecución de contrato:</strong> el tratamiento es necesario para la prestación del servicio SaaS contratado.</li>
              <li><strong>Consentimiento:</strong> para comunicaciones no esenciales, el usuario otorga su consentimiento al aceptar los Términos y Condiciones.</li>
              <li><strong>Interés legítimo:</strong> para el registro de auditoría y seguridad de la plataforma.</li>
              <li><strong>Obligación legal:</strong> cuando el tratamiento sea requerido por normativa vigente.</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>5. Conservación de los datos</h2>
            <p>
              Los datos personales se conservarán mientras el usuario mantenga una cuenta activa en StorSafe o mientras sea
              necesario para las finalidades descritas. Una vez solicitada la eliminación de la cuenta, los datos serán suprimidos
              o anonimizados en un plazo máximo de <strong>30 días hábiles</strong>, salvo que exista obligación legal de
              conservarlos por un período mayor.
            </p>
          </section>

          <section className="terms-section">
            <h2>6. Transferencia de datos</h2>
            <p>
              StorSafe utiliza proveedores de infraestructura en la nube (incluyendo Supabase y servicios de correo transaccional)
              para la prestación del servicio. Estos proveedores actúan como encargados del tratamiento bajo acuerdos de
              confidencialidad y seguridad. No se realizan transferencias internacionales de datos sin las garantías adecuadas
              conforme a la LOPDP.
            </p>
          </section>

          <section className="terms-section">
            <h2>7. Derechos del titular</h2>
            <p>
              Conforme a la Ley Orgánica de Protección de Datos Personales del Ecuador, el titular de los datos tiene derecho a:
            </p>
            <ul>
              <li><strong>Acceso:</strong> conocer qué datos personales suyos están siendo tratados.</li>
              <li><strong>Rectificación:</strong> solicitar la corrección de datos inexactos o incompletos.</li>
              <li><strong>Cancelación/Supresión:</strong> solicitar la eliminación de sus datos cuando ya no sean necesarios.</li>
              <li><strong>Oposición:</strong> oponerse al tratamiento de sus datos en determinadas circunstancias.</li>
              <li><strong>Portabilidad:</strong> recibir sus datos en un formato estructurado y de uso común.</li>
              <li><strong>Limitación:</strong> solicitar la restricción del tratamiento en ciertos supuestos.</li>
            </ul>
            <p>
              Para ejercer cualquiera de estos derechos, envíe una solicitud a:{' '}
              <a href="mailto:jcifuentes@storsafe.app" style={{ color: '#2FBF71' }}>jcifuentes@storsafe.app</a>.
              Responderemos en un plazo máximo de <strong>15 días hábiles</strong>.
            </p>
          </section>

          <section className="terms-section">
            <h2>8. Seguridad de los datos</h2>
            <p>
              AndeanSoft SAS implementa medidas técnicas y organizativas apropiadas para proteger los datos personales contra
              acceso no autorizado, alteración, divulgación o destrucción. Entre estas medidas se incluyen: cifrado en tránsito
              (TLS/HTTPS), control de acceso basado en roles, autenticación segura y registros de auditoría.
            </p>
          </section>

          <section className="terms-section">
            <h2>9. Cookies y tecnologías similares</h2>
            <p>
              StorSafe utiliza únicamente cookies técnicas esenciales para el funcionamiento de la sesión de usuario.
              No utilizamos cookies de rastreo, análisis comportamental de terceros ni publicidad.
            </p>
          </section>

          <section className="terms-section terms-section-last">
            <h2>10. Modificaciones a esta política</h2>
            <p>
              AndeanSoft SAS se reserva el derecho de actualizar esta Política de Privacidad. Cualquier cambio material
              será notificado a los usuarios registrados con al menos <strong>15 días de anticipación</strong> mediante
              correo electrónico o aviso dentro de la plataforma.
            </p>
            <p>
              Para consultas sobre privacidad y protección de datos:{' '}
              <a href="mailto:jcifuentes@storsafe.app" style={{ color: '#2FBF71' }}>jcifuentes@storsafe.app</a>
            </p>
          </section>

        </div>

        {onBack && (
          <div style={{ padding: '0 32px 28px' }}>
            <button onClick={onBack} style={{ background: 'none', border: '1px solid #334155', color: '#94a3b8', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem' }}>
              ← Volver
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ShieldIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}
